import React, { useEffect, useRef, useState, useCallback } from 'react';
import { usePage } from '@inertiajs/react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import * as turf from '@turf/turf';
import RBush from 'rbush';
import { schemeTableau10 } from 'd3-scale-chromatic';
import Layout from '../components/Layout';
import GeosampaLayerPanel from '../components/maps/GeosampaLayerPanel';
import { addGeosampaLayer, removeGeosampaLayer } from '../components/maps/geosampaLayers';
import { canViewPolo } from '../lib/permissions';

const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
// Tableau 10 gives the main map six peer categories with a tested categorical palette.
// Skip the yellow slot because it loses contrast on the light basemap.
const MAP_CATEGORY_PALETTE = [
  schemeTableau10[0],
  schemeTableau10[1],
  schemeTableau10[2],
  schemeTableau10[3],
  schemeTableau10[4],
  schemeTableau10[6],
];
const MAP_CATEGORY_COLORS = {
  'polo-nivel-1': MAP_CATEGORY_PALETTE[0],
  'polo-nivel-2': MAP_CATEGORY_PALETTE[1],
  'polo-nivel-3': MAP_CATEGORY_PALETTE[2],
  varejos: MAP_CATEGORY_PALETTE[3],
  'serie-historica-2020': MAP_CATEGORY_PALETTE[4],
  'serie-historica-2024': MAP_CATEGORY_PALETTE[5],
};
const POLO_LEVEL_COLORS = {
  1: MAP_CATEGORY_COLORS['polo-nivel-1'],
  2: MAP_CATEGORY_COLORS['polo-nivel-2'],
  3: MAP_CATEGORY_COLORS['polo-nivel-3'],
};
const VAREJOS_COLOR = MAP_CATEGORY_COLORS.varejos;
// Historical color identifies the year; level remains available in filters and hover.
const HISTORICAL_SERIES = [
  {
    year: 2020,
    sourceId: 'serie-historica-2020',
    url: '/geodata/serie-historica/polos-2020.geojson',
    color: MAP_CATEGORY_COLORS['serie-historica-2020'],
  },
  {
    year: 2024,
    sourceId: 'serie-historica-2024',
    url: '/geodata/serie-historica/polos-2024.geojson',
    color: MAP_CATEGORY_COLORS['serie-historica-2024'],
  },
];
const HISTORICAL_LEVELS = [1, 2];
const getHistoricalToggleId = (year, level) => `serie-historica-${year}-nivel-${level}`;
const getHistoricalRenderLayerIds = (toggleId) => [`${toggleId}-fill`, `${toggleId}-outline`];
const DEFAULT_HISTORICAL_VISIBILITY = HISTORICAL_SERIES.reduce((visibility, series) => {
  HISTORICAL_LEVELS.forEach(level => {
    visibility[getHistoricalToggleId(series.year, level)] = false;
  });
  return visibility;
}, {});
const DEFAULT_BUILDING_COLOR = '#ffffff';
const BUILDING_LAYER_ID = '3d-buildings-ofm';
const POLO_BUILDINGS_SOURCE = 'polo-buildings-source';
const POLO_BUILDINGS_LAYER = 'polo-buildings-layer';
const MIN_ZOOM_FOR_COLORING = 15;
const UPDATE_DEBOUNCE_MS = 150;
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const SAO_PAULO_VIEWBOX = '-46.83,-23.35,-46.35,-24.05';

const cleanHistoricalValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value).trim() || 'N/A';
};

const formatHistoricalNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'N/A';
  return number.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
};

async function addHistoricalSeriesLayers(mapInstance, layerVisibility) {
  const beforeLayerId = mapInstance.getLayer('varejos-layer') ? 'varejos-layer' : undefined;

  for (const series of HISTORICAL_SERIES) {
    const response = await axios.get(series.url);
    mapInstance.addSource(series.sourceId, {
      type: 'geojson',
      data: response.data,
    });

    HISTORICAL_LEVELS.forEach(level => {
      const toggleId = getHistoricalToggleId(series.year, level);
      const visibility = layerVisibility[toggleId] ? 'visible' : 'none';
      const filter = ['==', ['get', 'Classifica'], level];

      mapInstance.addLayer(
        {
          id: `${toggleId}-fill`,
          type: 'fill',
          source: series.sourceId,
          filter,
          layout: { visibility },
          paint: {
            'fill-color': series.color,
            'fill-opacity': 0.08,
          },
        },
        beforeLayerId
      );

      mapInstance.addLayer(
        {
          id: `${toggleId}-outline`,
          type: 'line',
          source: series.sourceId,
          filter,
          layout: { visibility },
          paint: {
            'line-color': series.color,
            'line-opacity': 0.9,
            'line-width': 2.5,
          },
        },
        beforeLayerId
      );
    });
  }
}

export default function Mapa() {
  const { auth } = usePage().props;
  const canView = canViewPolo(auth?.user);
  const canViewRef = useRef(canView);
  useEffect(() => { canViewRef.current = canView; }, [canView]);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const loteIndexRef = useRef(null); // R-tree spatial index for lotes
  const buildingCacheRef = useRef(new Map()); // Cache: geometryHash -> processed feature
  const lastBoundsKeyRef = useRef(null);
  const buildings3DVisibleRef = useRef(true);
  const visibleNiveisRef = useRef(new Set([1, 2, 3]));
  const addressMarkerRef = useRef(null);
  const addressSearchRequestRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeLayers, setActiveLayers] = useState(new Set());
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [is3D, setIs3D] = useState(true);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [addressSearchStatus, setAddressSearchStatus] = useState('idle');
  const [addressSearchError, setAddressSearchError] = useState('');

  const handleTogglePerspective = useCallback(() => {
    if (!map.current) return;
    setIs3D(prev => {
      const next = !prev;
      map.current.easeTo({
        pitch: next ? 45 : 0,
        bearing: next ? -17.6 : 0,
        duration: 600,
      });
      // Hide/show 3D building layers
      buildings3DVisibleRef.current = next;
      const vis = next ? 'visible' : 'none';
      [BUILDING_LAYER_ID, POLO_BUILDINGS_LAYER].forEach(id => {
        if (map.current.getLayer(id)) map.current.setLayoutProperty(id, 'visibility', vis);
      });
      // Sync the checkbox in the panel
      setAppLayerVisibility(v => ({ ...v, 'buildings-3d': next }));
      return next;
    });
  }, []);

  const [appLayerVisibility, setAppLayerVisibility] = useState({
    'polo-nivel-1': true,
    'polo-nivel-2': true,
    'polo-nivel-3': true,
    'varejos': false,
    'buildings-3d': true,
    ...DEFAULT_HISTORICAL_VISIBILITY,
  });

  const currentAppLayersForPanel = [
    { id: 'polo-nivel-1', label: 'Polo Nível 1', color: POLO_LEVEL_COLORS[1], visible: appLayerVisibility['polo-nivel-1'] },
    { id: 'polo-nivel-2', label: 'Polo Nível 2', color: POLO_LEVEL_COLORS[2], visible: appLayerVisibility['polo-nivel-2'] },
    { id: 'polo-nivel-3', label: 'Polo Nível 3', color: POLO_LEVEL_COLORS[3], visible: appLayerVisibility['polo-nivel-3'] },
    { id: 'varejos', label: 'Varejos', color: VAREJOS_COLOR, visible: appLayerVisibility['varejos'] },
    { id: 'buildings-3d', label: 'Edificações 3D', color: '#ffffff', visible: appLayerVisibility['buildings-3d'] },
  ];

  const historicalAppLayersForPanel = HISTORICAL_SERIES.flatMap(series => (
    HISTORICAL_LEVELS.map(level => {
      const id = getHistoricalToggleId(series.year, level);
      return {
        id,
        label: `${series.year} · Nível ${level}`,
        color: series.color,
        visible: appLayerVisibility[id],
        swatchType: 'line',
      };
    })
  ));

  const appLayerSections = [
    { id: 'camadas-do-mapa', label: 'Camadas do Mapa', layers: currentAppLayersForPanel },
    { id: 'serie-historica', label: 'Série Histórica', layers: historicalAppLayersForPanel },
  ];

  const handleToggleAppLayer = useCallback((layerId) => {
    if (!map.current) return;
    setAppLayerVisibility(prev => {
      const next = { ...prev, [layerId]: !prev[layerId] };
      const visible = next[layerId];

      if (layerId === 'buildings-3d') {
        buildings3DVisibleRef.current = visible;
        const vis = visible ? 'visible' : 'none';
        [BUILDING_LAYER_ID, POLO_BUILDINGS_LAYER].forEach(id => {
          if (map.current.getLayer(id)) map.current.setLayoutProperty(id, 'visibility', vis);
        });
      } else if (layerId === 'varejos') {
        if (map.current.getLayer('varejos-layer')) {
          map.current.setLayoutProperty('varejos-layer', 'visibility', visible ? 'visible' : 'none');
        }
      } else if (layerId.startsWith('serie-historica-')) {
        getHistoricalRenderLayerIds(layerId).forEach(id => {
          if (map.current.getLayer(id)) {
            map.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
          }
        });
      } else if (layerId.startsWith('polo-nivel-')) {
        // Rebuild visible nivels set
        const newVisibleNiveis = new Set();
        [1, 2, 3].forEach(n => { if (next[`polo-nivel-${n}`]) newVisibleNiveis.add(n); });
        visibleNiveisRef.current = newVisibleNiveis;

        // Filter polos and lotes to only show visible nivels
        const nivelsArray = [...newVisibleNiveis];
        const filter = nivelsArray.length === 0
          ? ['==', ['get', 'nivel'], -1]
          : ['in', ['get', 'nivel'], ['literal', nivelsArray]];
        ['polos-fill', 'polos-outline', 'lotes-fill', 'lotes-outline'].forEach(id => {
          if (map.current.getLayer(id)) map.current.setFilter(id, filter);
        });

        // Force building recolor on next idle — cache is still valid (stores nivel),
        // but the rendered features need updating with new visibility
        lastBoundsKeyRef.current = null;
      }

      return next;
    });
  }, []);

  const handleToggleLayer = useCallback((layerId) => {
    if (!map.current) return;
    setActiveLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
        removeGeosampaLayer(map.current, layerId);
      } else {
        next.add(layerId);
        addGeosampaLayer(map.current, layerId);
      }
      return next;
    });
  }, []);

  const clearAddressMarker = useCallback(() => {
    if (addressMarkerRef.current) {
      addressMarkerRef.current.remove();
      addressMarkerRef.current = null;
    }
  }, []);

  const selectAddressResult = useCallback((result) => {
    if (!map.current || !result) return;

    const lng = Number(result.lon);
    const lat = Number(result.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    clearAddressMarker();

    const popup = new maplibregl.Popup({ offset: 24 }).setText(result.display_name);
    addressMarkerRef.current = new maplibregl.Marker({ color: '#d946ef' })
      .setLngLat([lng, lat])
      .setPopup(popup)
      .addTo(map.current);

    if (Array.isArray(result.boundingbox) && result.boundingbox.length === 4) {
      const [south, north, west, east] = result.boundingbox.map(Number);
      if ([south, north, west, east].every(Number.isFinite) && west !== east && south !== north) {
        map.current.fitBounds(
          [[west, south], [east, north]],
          { padding: 80, maxZoom: 17, duration: 700 }
        );
      } else {
        map.current.flyTo({ center: [lng, lat], zoom: 17, duration: 700 });
      }
    } else {
      map.current.flyTo({ center: [lng, lat], zoom: 17, duration: 700 });
    }

    addressMarkerRef.current.togglePopup();
  }, [clearAddressMarker]);

  const handleAddressSearch = useCallback(async (event) => {
    event?.preventDefault();

    const rawQuery = addressQuery.trim();
    if (!rawQuery) return;

    if (addressSearchRequestRef.current) {
      addressSearchRequestRef.current.abort();
    }

    const controller = new AbortController();
    addressSearchRequestRef.current = controller;
    setAddressSearchStatus('searching');
    setAddressSearchError('');
    setAddressResults([]);

    const query = /\bs[aã]o\s+paulo\b|\bsp\b|\bbrasil\b|\bbrazil\b/i.test(rawQuery)
      ? rawQuery
      : `${rawQuery}, São Paulo, SP, Brasil`;
    const params = new URLSearchParams({
      q: query,
      format: 'jsonv2',
      addressdetails: '1',
      limit: '5',
      countrycodes: 'br',
      viewbox: SAO_PAULO_VIEWBOX,
      bounded: '1',
      'accept-language': 'pt-BR',
    });

    try {
      const response = await fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('Falha na busca de endereço');
      }

      const results = (await response.json())
        .filter(result => Number.isFinite(Number(result.lat)) && Number.isFinite(Number(result.lon)));

      setAddressResults(results);
      if (results.length > 0) {
        setAddressSearchStatus('results');
        selectAddressResult(results[0]);
      } else {
        clearAddressMarker();
        setAddressSearchStatus('empty');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setAddressSearchError('Não foi possível buscar esse endereço.');
      setAddressSearchStatus('error');
    } finally {
      if (addressSearchRequestRef.current === controller) {
        addressSearchRequestRef.current = null;
      }
    }
  }, [addressQuery, clearAddressMarker, selectAddressResult]);

  const currentLegendEntries = [
    appLayerVisibility['polo-nivel-1'] && {
      id: 'polo-nivel-1',
      label: 'Polo Nível 1',
      type: 'line',
      color: POLO_LEVEL_COLORS[1],
    },
    appLayerVisibility['polo-nivel-2'] && {
      id: 'polo-nivel-2',
      label: 'Polo Nível 2',
      type: 'line',
      color: POLO_LEVEL_COLORS[2],
    },
    appLayerVisibility['polo-nivel-3'] && {
      id: 'polo-nivel-3',
      label: 'Polo Nível 3',
      type: 'line',
      color: POLO_LEVEL_COLORS[3],
    },
    appLayerVisibility['varejos'] && {
      id: 'varejos',
      label: 'Varejo',
      type: 'circle',
      color: VAREJOS_COLOR,
    },
  ].filter(Boolean);

  const historicalLegendEntries = HISTORICAL_SERIES.map(series => {
    const visible = HISTORICAL_LEVELS.some(level => (
      appLayerVisibility[getHistoricalToggleId(series.year, level)]
    ));
    if (!visible) return null;

    return {
      id: `serie-historica-${series.year}`,
      label: String(series.year),
      type: 'line',
      color: series.color,
    };
  }).filter(Boolean);

  const legendEntries = [...currentLegendEntries, ...historicalLegendEntries];

  // Suppress MapLibre AbortError noise from cancelled tile fetches
  useEffect(() => {
    const handler = (e) => {
      if (e.reason?.name === 'AbortError' &&
          e.reason?.stack?.includes('abortTile')) {
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  useEffect(() => {
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: OPENFREEMAP_STYLE,
      center: [-46.6259, -23.6],
      zoom: 11,
      pitch: 45,
      bearing: -17.6,
      canvasContextAttributes: { antialias: true },
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    map.current.on('load', async () => {
      setMapLoaded(true);

      // Hide the base style's buildings since we'll add our own 3D buildings
      const style = map.current.getStyle();
      style.layers.forEach((layer) => {
        if (layer.id.toLowerCase().includes('building') &&
            layer.id !== '3d-buildings-ofm') {
          map.current.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });

      try {
        // Fetch varejos data
        const varejosResponse = await axios.get('/varejo_redes.json');
        map.current.addSource('varejos', {
          type: 'geojson',
          data: varejosResponse.data,
        });

        map.current.addLayer({
          id: 'varejos-layer',
          type: 'circle',
          source: 'varejos',
          layout: {
            visibility: appLayerVisibility['varejos'] ? 'visible' : 'none',
          },
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2, 15, 6],
            'circle-color': VAREJOS_COLOR,
            'circle-opacity': 0.8,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });

        // Fetch polos data
        const polosResponse = await axios.get('/polos.json');
        const polosData = polosResponse.data;

        map.current.addSource('polos', {
          type: 'geojson',
          data: polosData,
        });

        map.current.addLayer({
          id: 'polos-fill',
          type: 'fill',
          source: 'polos',
          paint: {
            'fill-color': [
              'match',
              ['get', 'nivel'],
              1, POLO_LEVEL_COLORS[1],
              2, POLO_LEVEL_COLORS[2],
              3, POLO_LEVEL_COLORS[3],
              POLO_LEVEL_COLORS[1],
            ],
            'fill-opacity': 0.15,
          },
        });

        map.current.addLayer({
          id: 'polos-outline',
          type: 'line',
          source: 'polos',
          paint: {
            'line-color': [
              'match',
              ['get', 'nivel'],
              1, POLO_LEVEL_COLORS[1],
              2, POLO_LEVEL_COLORS[2],
              3, POLO_LEVEL_COLORS[3],
              POLO_LEVEL_COLORS[1],
            ],
            'line-width': 3,
          },
        });

        // Fetch lotes data and build spatial index of centroids
        const lotesResponse = await axios.get('/lotes/in_polo.json');
        const lotesData = lotesResponse.data;

        // Build R-tree index for lotes (stores both centroid and polygon for different logic)
        const loteIndex = new RBush();
        const loteItems = [];

        for (const feature of lotesData.features) {
          if (!feature.geometry) continue;
          try {
            const centroid = turf.centroid(feature);
            const [lng, lat] = centroid.geometry.coordinates;
            const bbox = turf.bbox(feature);
            loteItems.push({
              minX: bbox[0],
              minY: bbox[1],
              maxX: bbox[2],
              maxY: bbox[3],
              point: [lng, lat],
              feature: feature // Store full feature for intersects check
            });
          } catch (e) {
            // Skip invalid geometries
          }
        }

        loteIndex.load(loteItems);
        loteIndexRef.current = loteIndex;
        buildingCacheRef.current.clear();
        console.log('[BuildingColors] Built R-tree index with', loteItems.length, 'lotes');

        map.current.addSource('lotes', {
          type: 'geojson',
          data: lotesData,
        });

        map.current.addLayer({
          id: 'lotes-fill',
          type: 'fill',
          source: 'lotes',
          paint: {
            'fill-color': [
              'match',
              ['get', 'nivel'],
              1, POLO_LEVEL_COLORS[1],
              2, POLO_LEVEL_COLORS[2],
              3, POLO_LEVEL_COLORS[3],
              POLO_LEVEL_COLORS[2],
            ],
            'fill-opacity': 0.3,
          },
        });

        map.current.addLayer({
          id: 'lotes-outline',
          type: 'line',
          source: 'lotes',
          paint: {
            'line-color': [
              'match',
              ['get', 'nivel'],
              1, POLO_LEVEL_COLORS[1],
              2, POLO_LEVEL_COLORS[2],
              3, POLO_LEVEL_COLORS[3],
              POLO_LEVEL_COLORS[2],
            ],
            'line-width': 1,
          },
        });

        await addHistoricalSeriesLayers(map.current, appLayerVisibility);

        // Add 3D buildings from OpenFreeMap
        const layers = map.current.getStyle().layers;
        let labelLayerId;
        for (let i = 0; i < layers.length; i++) {
          if (layers[i].type === 'symbol' && layers[i].layout['text-field']) {
            labelLayerId = layers[i].id;
            break;
          }
        }

        map.current.addSource('openfreemap-buildings', {
          url: 'https://tiles.openfreemap.org/planet',
          type: 'vector',
        });

        // Add 3D buildings layer with default white color
        map.current.addLayer(
          {
            id: BUILDING_LAYER_ID,
            source: 'openfreemap-buildings',
            'source-layer': 'building',
            type: 'fill-extrusion',
            minzoom: 14,
            filter: ['!=', ['get', 'hide_3d'], true],
            paint: {
              'fill-extrusion-color': DEFAULT_BUILDING_COLOR,
              'fill-extrusion-height': [
                'interpolate',
                ['linear'],
                ['zoom'],
                14, 0,
                15, ['coalesce', ['get', 'render_height'], 10],
              ],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': 1,
            },
          },
          labelLayerId
        );

        console.log('[BuildingColors] 3D buildings layer added');

        // Add empty source and layer for polo buildings (rendered on top in blue)
        map.current.addSource(POLO_BUILDINGS_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });

        // Single layer for ALL buildings when zoomed in - color driven by 'inPolo' property
        // Polo buildings are slightly expanded (XY via buffer, Z via height/base offset)
        map.current.addLayer({
          id: POLO_BUILDINGS_LAYER,
          source: POLO_BUILDINGS_SOURCE,
          type: 'fill-extrusion',
          minzoom: MIN_ZOOM_FOR_COLORING,
          paint: {
            'fill-extrusion-color': [
              'case',
              ['==', ['get', 'inPolo'], true],
              [
                'match',
                ['get', 'poloNivel'],
                1, POLO_LEVEL_COLORS[1],
                2, POLO_LEVEL_COLORS[2],
                3, POLO_LEVEL_COLORS[3],
                POLO_LEVEL_COLORS[2],
              ],
              DEFAULT_BUILDING_COLOR
            ],
            'fill-extrusion-height': [
              'case',
              ['==', ['get', 'inPolo'], true],
              ['+', ['coalesce', ['get', 'render_height'], 10], 0.5], // Slightly taller
              ['coalesce', ['get', 'render_height'], 10]
            ],
            'fill-extrusion-base': [
              'case',
              ['==', ['get', 'inPolo'], true],
              ['-', ['coalesce', ['get', 'render_min_height'], 0], 0.3], // Slightly lower base
              ['coalesce', ['get', 'render_min_height'], 0]
            ],
            'fill-extrusion-opacity': 1,
          },
        });

        // Optimized function to update buildings based on viewport
        const updateBuildingColors = () => {
          // Skip processing entirely if 3D buildings are toggled off
          if (!buildings3DVisibleRef.current) return;

          const currentZoom = map.current.getZoom();
          const source = map.current.getSource(POLO_BUILDINGS_SOURCE);
          const loteIndex = loteIndexRef.current;

          // Only process when zoomed in enough
          if (currentZoom < MIN_ZOOM_FOR_COLORING) {
            // Show vector tile layer, clear GeoJSON layer
            map.current.setLayoutProperty(BUILDING_LAYER_ID, 'visibility', 'visible');
            if (source) {
              source.setData({ type: 'FeatureCollection', features: [] });
            }
            lastBoundsKeyRef.current = null;
            return;
          }

          const bounds = map.current.getBounds();
          const boundsKey = `${bounds.getWest().toFixed(3)},${bounds.getSouth().toFixed(3)},${bounds.getEast().toFixed(3)},${bounds.getNorth().toFixed(3)}`;

          // Skip if bounds haven't changed
          if (lastBoundsKeyRef.current === boundsKey) return;

          if (!loteIndex) {
            console.log('[BuildingColors] No lote index available');
            return;
          }

          // Ensure vector layer is visible for querying (may have been hidden)
          map.current.setLayoutProperty(BUILDING_LAYER_ID, 'visibility', 'visible');

          // Query buildings
          const allBuildings = map.current.queryRenderedFeatures({ layers: [BUILDING_LAYER_ID] });

          if (allBuildings.length === 0) {
            // Keep vector layer visible if no buildings to process
            return;
          }

          // Process buildings with caching - only compute inPolo for new buildings
          const processedBuildings = [];
          const seenThisFrame = new Set();
          const cache = buildingCacheRef.current;
          let cacheHits = 0, cacheMisses = 0;

          for (const building of allBuildings) {
            if (!building.geometry) continue;

            // Split MultiPolygon into individual Polygons for more accurate checking
            const geometries = building.geometry.type === 'MultiPolygon'
              ? building.geometry.coordinates.map(coords => ({ type: 'Polygon', coordinates: coords }))
              : [building.geometry];

            for (const geom of geometries) {
              // Use geometry hash as cache key
              const geoKey = JSON.stringify(geom);
              if (seenThisFrame.has(geoKey)) continue;
              seenThisFrame.add(geoKey);

              // Check cache first
              if (cache.has(geoKey)) {
                processedBuildings.push(cache.get(geoKey));
                cacheHits++;
                continue;
              }

              // Cache miss - find which nivel this building belongs to (0 = not in polo)
              cacheMisses++;
              let poloNivel = 0;

              // Create a feature for the individual polygon
              const polyFeature = { type: 'Feature', geometry: geom, properties: {} };

              // Get bbox for R-tree query
              const bbox = turf.bbox(polyFeature);
              const [minX, minY, maxX, maxY] = bbox;

              // R-tree spatial query - O(log n)
              const candidateLotes = loteIndex.search({
                minX, minY, maxX, maxY
              });

              // Check if building intersects with any lote polygon
              for (const item of candidateLotes) {
                try {
                  if (turf.booleanIntersects(polyFeature, item.feature)) {
                    poloNivel = item.feature.properties?.nivel || 0;
                    break;
                  }
                } catch (e) {
                  // Skip invalid geometries
                }
              }

              // For polo buildings, scale geometry slightly outward from centroid
              let geometry = geom;
              if (poloNivel > 0 && geometry.type === 'Polygon' && geometry.coordinates?.[0]) {
                try {
                  const coords = geometry.coordinates[0];
                  let cx = 0, cy = 0;
                  for (const [x, y] of coords) { cx += x; cy += y; }
                  cx /= coords.length;
                  cy /= coords.length;
                  const scale = 1.0005;
                  const scaledCoords = coords.map(([x, y]) => [
                    cx + (x - cx) * scale,
                    cy + (y - cy) * scale
                  ]);
                  geometry = { type: 'Polygon', coordinates: [scaledCoords] };
                } catch (e) {
                  // Keep original geometry if scaling fails
                }
              }

              const feature = {
                type: 'Feature',
                geometry,
                properties: {
                  ...building.properties,
                  poloNivel
                }
              };

              // Store in cache (nivel is stable, visibility is checked at render time)
              cache.set(geoKey, feature);
              processedBuildings.push(feature);
            }
          }

          // Apply nivel visibility: set inPolo based on whether that nivel is toggled on
          const visibleNiveis = visibleNiveisRef.current;
          const renderedFeatures = processedBuildings.map(f => {
            const nivel = f.properties.poloNivel || 0;
            const inPolo = nivel > 0 && visibleNiveis.has(nivel);
            if (f.properties.inPolo === inPolo) return f;
            return { ...f, properties: { ...f.properties, inPolo } };
          });

          // Update the GeoJSON source FIRST
          if (source) {
            source.setData({
              type: 'FeatureCollection',
              features: renderedFeatures
            });
          }

          // THEN hide the vector layer (GeoJSON is now ready)
          map.current.setLayoutProperty(BUILDING_LAYER_ID, 'visibility', 'none');

          // Mark bounds as processed
          lastBoundsKeyRef.current = boundsKey;

          const poloCount = renderedFeatures.filter(f => f.properties.inPolo).length;
          console.log(`[BuildingColors] ${renderedFeatures.length} buildings (${poloCount} polo) | cache: ${cacheHits} hits, ${cacheMisses} misses`);
        };

        // Use 'idle' event - fires after map finishes rendering (tiles loaded)
        map.current.on('idle', updateBuildingColors);

        // Add hover interactions
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        HISTORICAL_SERIES.forEach(series => {
          HISTORICAL_LEVELS.forEach(level => {
            const layerId = `${getHistoricalToggleId(series.year, level)}-fill`;
            map.current.on('mouseenter', layerId, (e) => {
              map.current.getCanvas().style.cursor = 'pointer';
              const props = e.features[0].properties;
              const nome = cleanHistoricalValue(props.NomeCurto || props.Nome);
              popup.setLngLat(e.lngLat).setHTML(`
                <div style="padding: 8px; font-size: 12px;">
                  <strong>Série Histórica ${series.year}</strong><br/>
                  <span style="color: #666;">${nome}</span><br/>
                  Nível ${props.Classifica || level}<br/>
                  Lojas: ${formatHistoricalNumber(props.QTD_LOJAS)}<br/>
                  Densidade: ${formatHistoricalNumber(props.DENS_LOJAS)}
                </div>
              `).addTo(map.current);
            });

            map.current.on('mouseleave', layerId, () => {
              map.current.getCanvas().style.cursor = '';
              popup.remove();
            });
          });
        });

        map.current.on('mouseenter', 'varejos-layer', (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          const props = e.features[0].properties;
          popup.setLngLat(e.lngLat).setHTML(`
            <div style="padding: 8px; font-size: 12px;">
              <strong>${props.marca || 'N/A'}</strong><br/>
              <span style="color: #666;">${props.classificacao || 'N/A'}</span><br/>
              ${props.endereco || ''}
            </div>
          `).addTo(map.current);
        });

        map.current.on('mouseleave', 'varejos-layer', () => {
          map.current.getCanvas().style.cursor = '';
          popup.remove();
        });

        map.current.on('mouseenter', 'polos-fill', (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          const props = e.features[0].properties;
          const baseName = `${props.lg_tipo || ''} ${props.lg_titulo || ''} ${props.lg_prep || ''} ${props.lg_nome || ''}`.trim();
          const poloNome = props.distincao_nome ? `${baseName} - ${props.distincao_nome}` : baseName;
          popup.setLngLat(e.lngLat).setHTML(`
            <div style="padding: 8px; font-size: 12px;">
              <strong>POLO NÍVEL ${props.nivel}</strong><br/>
              <span style="color: #666;">${poloNome}</span>
            </div>
          `).addTo(map.current);
        });

        map.current.on('mouseleave', 'polos-fill', () => {
          map.current.getCanvas().style.cursor = '';
          popup.remove();
        });

        map.current.on('mouseenter', 'lotes-fill', (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          const props = e.features[0].properties;
          popup.setLngLat(e.lngLat).setHTML(`
            <div style="padding: 8px; font-size: 12px;">
              <strong>Lote</strong><br/>
              <span style="color: #666;">Nível ${props.nivel || 'N/A'}</span><br/>
              <em>Clique para ver detalhes</em>
            </div>
          `).addTo(map.current);
        });

        map.current.on('mouseleave', 'lotes-fill', () => {
          map.current.getCanvas().style.cursor = '';
          popup.remove();
        });

        map.current.on('click', 'lotes-fill', (e) => {
          const props = e.features[0].properties;
          if (props.polo_id && canViewRef.current) {
            window.location.href = `/polos/${props.polo_id}`;
          }
        });

      } catch (error) {
        console.error('Error loading map data:', error);
      }
    });

    return () => {
      if (addressSearchRequestRef.current) {
        addressSearchRequestRef.current.abort();
        addressSearchRequestRef.current = null;
      }
      clearAddressMarker();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [clearAddressMarker]);

  return (
    <Layout fullWidth>
      <div style={{ position: 'relative', height: 'calc(100vh - 200px)', width: '100%', minHeight: '500px' }}>
        <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />

        {/* GeoSampa layer panel */}
        <GeosampaLayerPanel
          isOpen={layerPanelOpen}
          onOpen={() => setLayerPanelOpen(true)}
          onClose={() => setLayerPanelOpen(false)}
          activeLayers={activeLayers}
          onToggleLayer={handleToggleLayer}
          appLayerSections={appLayerSections}
          onToggleAppLayer={handleToggleAppLayer}
        />

        {/* Address search */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 58,
            width: 'min(420px, calc(100% - 116px))',
            zIndex: 2,
          }}
        >
          <form
            onSubmit={handleAddressSearch}
            style={{
              display: 'flex',
              alignItems: 'stretch',
              backgroundColor: 'rgba(255,255,255,0.96)',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            }}
          >
            <input
              type="search"
              value={addressQuery}
              onChange={(event) => {
                setAddressQuery(event.target.value);
                if (addressSearchStatus !== 'idle') {
                  setAddressSearchStatus('idle');
                  setAddressSearchError('');
                }
              }}
              placeholder="Buscar endereço"
              aria-label="Buscar endereço"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                padding: '8px 10px',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={addressSearchStatus === 'searching'}
              style={{
                border: 0,
                borderLeft: '1px solid #d1d5db',
                backgroundColor: addressSearchStatus === 'searching' ? '#e5e7eb' : '#075669',
                color: addressSearchStatus === 'searching' ? '#4b5563' : '#fff',
                padding: '0 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: addressSearchStatus === 'searching' ? 'default' : 'pointer',
              }}
            >
              {addressSearchStatus === 'searching' ? 'Buscando' : 'Buscar'}
            </button>
          </form>

          {(addressSearchStatus === 'empty' || addressSearchStatus === 'error') && (
            <div
              style={{
                marginTop: 4,
                padding: '7px 9px',
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.96)',
                color: '#374151',
                fontSize: 12,
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }}
            >
              {addressSearchStatus === 'empty' ? 'Nenhum endereço encontrado.' : addressSearchError}
            </div>
          )}

          {addressResults.length > 1 && addressSearchStatus === 'results' && (
            <div
              style={{
                marginTop: 4,
                maxHeight: 180,
                overflowY: 'auto',
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.98)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              }}
            >
              {addressResults.map((result) => (
                <button
                  key={result.place_id}
                  type="button"
                  onClick={() => selectAddressResult(result)}
                  style={{
                    display: 'block',
                    width: '100%',
                    border: 0,
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: 'transparent',
                    padding: '8px 10px',
                    textAlign: 'left',
                    fontSize: 12,
                    color: '#1f2937',
                    cursor: 'pointer',
                  }}
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 2D/3D toggle */}
        <button
          type="button"
          onClick={handleTogglePerspective}
          style={{
            position: 'absolute',
            top: 10,
            left: layerPanelOpen ? 300 : 50,
            backgroundColor: 'rgba(255,255,255,0.9)',
            padding: '6px 12px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            zIndex: 1,
            border: '1px solid #ccc',
            cursor: 'pointer',
            transition: 'left 0.2s ease',
          }}
          title={is3D ? 'Mudar para vista 2D' : 'Mudar para vista 3D'}
        >
          {is3D ? '2D' : '3D'}
        </button>

        {/* 3D controls hint — only in 3D mode */}
        {is3D && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              left: layerPanelOpen ? 348 : 98,
              backgroundColor: 'rgba(255,255,255,0.9)',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 11,
              color: '#555',
              zIndex: 1,
              transition: 'left 0.2s ease',
            }}
          >

            Rotacionar: botão direito + arrastar<br/>
            Inclinar: Ctrl + arrastar
          </div>
        )}

        {/* Legend — only for visible app layers; GeoSampa legends live in the layer panel */}
        {!layerPanelOpen && legendEntries.length > 0 && (
          <div
            style={{
              position: 'absolute',
              bottom: 100,
              left: 60,
              backgroundColor: 'rgba(255, 255, 255, 0.82)',
              padding: 12,
              borderRadius: 4,
              minWidth: 190,
              zIndex: 1,
              boxShadow: '0 1px 5px rgba(0,0,0,0.16)',
            }}
          >
            <h6 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 700 }}>Legenda</h6>
            {legendEntries.map((entry) => (
              <div key={entry.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                {entry.type === 'circle' ? (
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 999,
                      backgroundColor: entry.color,
                      border: '1px solid #fff',
                      margin: '0 21px 0 3px',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 0,
                      borderTopWidth: entry.width || 3,
                      borderTopStyle: 'solid',
                      borderTopColor: entry.color,
                      marginRight: 8,
                    }}
                  />
                )}
                <span style={{ fontSize: 13 }}>{entry.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Attribution */}
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            fontSize: 10,
            color: '#666',
            zIndex: 1,
          }}
        >
          MapLibre GL JS + OpenFreeMap + OpenStreetMap
        </div>
      </div>
    </Layout>
  );
}
